import { Link } from 'react-router-dom';
import { ExternalLinkIcon } from 'lucide-react';
import { Field } from '../ui/Field';
import { ProductPicker } from '../ui/ProductPicker';
import { useAdmin } from '../AdminContext';
import { templateByKey } from '../marketing/marketingOptions';
import { PRODUCTS_MAX } from './newsletterRules';
import { btnGhost, inputCls } from '../ui/styles';
import { isPhysical } from '../../core/rules/stock';
import type { NewsletterBlock } from '../../core/types/newsletter';
type Props = {
  block: NewsletterBlock;
  onChange: (block: NewsletterBlock) => void;
};

/** De velden van één blok. Het soort blok bepaalt wat er staat; de rest komt uit het beheer. */
export function BlockFields({
  block,
  onChange
}: Props) {
  const {
    marketing,
    catalog
  } = useAdmin();
  switch (block.kind) {
    case 'design':
      {
        // E-mailontwerpen eerst: die zijn ervoor gemaakt. Print en stories passen niet in een mail.
        const usable = marketing.designs.filter(d => templateByKey(d.template).channel !== 'Print' && d.template !== 'story');
        const email = usable.filter(d => templateByKey(d.template).channel === 'E-mail');
        const social = usable.filter(d => templateByKey(d.template).channel !== 'E-mail');
        return <div className="space-y-3" data-id="element-3186" data-mp="3fekggvrd7yyr">
          <Field label="Ontwerp" hint="Kleur, kop, tekst en foto komen uit het ontwerp. De knop linkt naar de QR-link van het ontwerp." data-id="element-3187" data-mp="3fcq3c3mc5b58">
            <select value={block.designId} onChange={e => onChange({
              ...block,
              designId: e.target.value
            })} className={inputCls} data-id="element-3188" data-mp="2mplzi1yzb2v1">
              <option value="" data-id="element-3189" data-mp="2ht59114b4ces">Kies een ontwerp</option>
              {email.length > 0 && <optgroup label="E-mail" data-id="element-3190" data-mp="2whrm0w6gos2u">
                  {email.map(d => <option key={d.id} value={d.id} data-id="element-3191" data-mp="2roq4egeveefk">
                      {d.name}
                    </option>)}
                </optgroup>}
              {social.length > 0 && <optgroup label="Social" data-id="element-3192" data-mp="1z0b1utcxdol4">
                  {social.map(d => <option key={d.id} value={d.id} data-id="element-3193" data-mp="2oo6rs4dxkztq">
                      {d.name}
                    </option>)}
                </optgroup>}
            </select>
          </Field>
          {block.designId && <Link to={`/be/ontwerpen/${block.designId}`} className={btnGhost} data-id="element-3194" data-mp="2xjmjaer7r2w5">
              Ontwerp aanpassen
              <ExternalLinkIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-3195" data-mp="25q40fixwuqsr" />
            </Link>}
        </div>;
      }
    case 'text':
      return <div className="space-y-4" data-id="element-3196" data-mp="29tiomww06dha">
          <Field label="Tussenkop" hint="Optioneel." data-id="element-3197" data-mp="2eqivvb4l9m9z">
            <input value={block.heading} onChange={e => onChange({
            ...block,
            heading: e.target.value
          })} className={inputCls} data-id="element-3198" data-mp="2esv8r9vhp6g6" />
          </Field>
          <Field label="Tekst" hint="Een witregel begint een nieuwe alinea. {firstName} en {city} vullen zich vanzelf in." data-id="element-3199" data-mp="3q6qpuo5yfpa1">
            <textarea value={block.body} onChange={e => onChange({
            ...block,
            body: e.target.value
          })} rows={6} className={`${inputCls} resize-y leading-relaxed`} data-id="element-3200" data-mp="3v3h4r860wetv" />
          </Field>
        </div>;
    case 'products':
      return <div className="space-y-4" data-id="element-3201" data-mp="19q2jcd5m5gwr">
          <Field label="Kop boven de producten" data-id="element-3202" data-mp="14ssoghzggd1j">
            <input value={block.heading} onChange={e => onChange({
            ...block,
            heading: e.target.value
          })} className={inputCls} data-id="element-3203" data-mp="14s9ebng1qsy2" />
          </Field>
          <Field label="Producten" as="div" count={{
          value: block.productIds.length,
          max: PRODUCTS_MAX
        }} hint="Foto, naam en prijs komen uit de webshop, dus ze kloppen ook als je een prijs aanpast." data-id="element-3204" data-mp="vs499jybs6n2">
            <ProductPicker value={block.productIds} onChange={productIds => onChange({
            ...block,
            productIds
          })} products={catalog.products.filter(isPhysical)} ordered data-id="element-3205" data-mp="lu6iopsztxra" />
          </Field>
        </div>;
    case 'article':
      return <Field label="Artikel" hint="Foto, titel en korte tekst komen uit het journal." data-id="element-3206" data-mp="2o842r5g59rfo">
          <select value={block.slug} onChange={e => onChange({
          ...block,
          slug: e.target.value
        })} className={inputCls} data-id="element-3207" data-mp="2t7glpf3waecz">
            <option value="" data-id="element-3208" data-mp="2eb96kt7cg34k">Kies een artikel</option>
            {catalog.articles.map(a => <option key={a.slug} value={a.slug} data-id="element-3209" data-mp="2j8rcsbtz848v">
                {a.title}
              </option>)}
          </select>
        </Field>;
    case 'button':
      return <div className="grid gap-4 sm:grid-cols-2" data-id="element-3210" data-mp="2a9e0lbegx1kg">
          <Field label="Tekst op de knop" data-id="element-3211" data-mp="2aayxv8s62ym0">
            <input value={block.label} onChange={e => onChange({
            ...block,
            label: e.target.value
          })} className={inputCls} data-id="element-3212" data-mp="1vkhvnn7pk4o2" />
          </Field>
          <Field label="Link" data-id="element-3213" data-mp="20jcg0tlbq1aa">
            <input type="url" inputMode="url" value={block.url} onChange={e => onChange({
            ...block,
            url: e.target.value
          })} placeholder="https://beautybewust.nl/webshop" className={inputCls} data-id="element-3214" data-mp="1mj9zpbsnzfx5" />
          </Field>
        </div>;
  }
}